import React, { useState } from 'react';
import {
  Table,
  Button,
  Space,
  Input,
  Modal,
  Typography,
  message,
  Tag,
  Tooltip,
  Card,
  Statistic,
  Row,
  Col,
  Drawer,
  List
} from 'antd';
import {
  SearchOutlined,
  MailOutlined,
  CheckCircleOutlined,
  StopOutlined,
  DollarOutlined,
  FileTextOutlined,
  ShoppingOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import { customerService, Customer } from '../../services/customerService';
import CustomerModal from '../../components/customers/CustomerModal';
import styles from '../customers/CustomersPage.module.css';

const { Text } = Typography;
const { Search } = Input;

const CustomersPage: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [filters] = useState({
    status: '',
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

  const queryClient = useQueryClient();

  // Fetch data
  const { data: customersData = { data: [], pagination: { total: 0 } }, isLoading } = useQuery(
    ['customers', searchText, filters, pagination.current, pagination.pageSize],
    () => customerService.getCustomers({
        search: searchText,
        status: filters.status,
        page: pagination.current,
        limit: pagination.pageSize
    }),
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  // Fetch toàn bộ khách hàng để đếm active/inactive
  const { data: allCustomersData } = useQuery(
    ['customers', searchText, filters, 'all'],
    () => customerService.getCustomers({
      search: searchText,
      status: '',
      page: 1,
      limit: 10000
    }),
    { keepPreviousData: true }
  );
  const allCustomers = allCustomersData?.data || [];
  const activeCustomers = allCustomers.filter((c: Customer) => c.status === 'active').length;
  const inactiveCustomers = allCustomers.filter((c: Customer) => c.status === 'inactive').length;

  const customers = customersData.data || [];

  // Fetch detailed customer for viewing
  const { data: viewDetailData, isLoading: isLoadingViewDetail } = useQuery(
    ['customer-detail', selectedCustomer?.id],
    () => customerService.getCustomerDetail(selectedCustomer!.id),
    {
      enabled: !!selectedCustomer?.id && drawerVisible,
      staleTime: 1 * 60 * 1000,
    }
  );

  // Mutations
  const createMutation = useMutation(
    (data: any) => customerService.createCustomer(data),
    {
      onSuccess: () => {
        message.success('Tạo khách hàng thành công!');
        queryClient.invalidateQueries('customers');
        setModalVisible(false);
      },
      onError: (error: any) => {
        console.error('Create customer error:', error);
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi tạo khách hàng');
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: any }) => customerService.updateCustomer(id, data),
    {
      onSuccess: () => {
        message.success('Cập nhật khách hàng thành công!');
        queryClient.invalidateQueries('customers');
        setModalVisible(false);
        setEditingCustomer(null);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật khách hàng');
      },
    }
  );

  const deleteMutation = useMutation(
    (id: number) => customerService.deleteCustomer(id),
    {
      onSuccess: () => {
        message.success('Xóa khách hàng thành công!');
        queryClient.invalidateQueries('customers');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi xóa khách hàng');
      },
    }
  );

  // Handlers
  const handleAdd = () => {
    setEditingCustomer(null);
    setModalVisible(true);
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setModalVisible(true);
  };

  const handleView = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDrawerVisible(true);
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Xác nhận xóa khách hàng',
      content: 'Bạn có chắc chắn muốn xóa khách hàng này? Hành động này không thể hoàn tác.',
      okText: 'Xóa',
      cancelText: 'Hủy',
      okType: 'danger',
      onOk: () => deleteMutation.mutate(id),
    });
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingCustomer(null);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingCustomer) {
        await updateMutation.mutateAsync({ id: editingCustomer.id, data: values });
      } else {
        await createMutation.mutateAsync(values);
      }
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };



  // Filtered data
  const filteredCustomers = customers.filter((customer: Customer) => {
    const matchesStatus = !filters.status || customer.status === filters.status;
    return matchesStatus;
  });

  const columns = [
    {
      title: 'Khách hàng',
      key: 'customer_info',
      width: 220,
      fixed: 'left' as const,
      sorter: (a: Customer, b: Customer) => a.name.localeCompare(b.name, 'vi'),
      render: (_: any, record: Customer) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ 
            fontWeight: '600', 
            fontSize: '15px',
            color: '#1f2937',
            lineHeight: '1.2'
          }}>
            {record.name}
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: '#6b7280',
            fontWeight: '400'
          }}>
            Mã KH: {record.code}
          </div>
          {record.contact_person && (
            <div style={{ 
              fontSize: '12px', 
              color: '#6b7280',
              fontWeight: '400'
            }}>
              LH: {record.contact_person}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Liên hệ',
      key: 'contact',
      width: 160,
      render: (_: any, record: Customer) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {record.phone && (
            <div style={{ 
              fontSize: '13px',
              fontWeight: '500',
              color: '#374151'
            }}>
              {record.phone}
            </div>
          )}
          {record.email && (
            <div style={{ 
              fontSize: '12px', 
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <MailOutlined style={{ fontSize: '10px' }} />
              {record.email}
            </div>
          )}
          {record.address && (
            <div style={{ 
              fontSize: '12px', 
              color: '#6b7280',
              fontWeight: '400'
            }}>
              {record.address}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Thông tin',
      key: 'info',
      width: 140,
      render: (_: any, record: Customer) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            fontSize: '13px',
            color: '#374151'
          }}>
            <i className="fas fa-shopping-cart" style={{ color: '#6b7280', fontSize: '12px' }} />
            <span style={{ fontWeight: '500' }}>{record.total_orders || 0} đơn</span>
          </div>
          <div style={{ 
            fontSize: '14px',
            fontWeight: '600',
            color: '#059669'
          }}>
            {(record.total_amount || 0).toLocaleString('vi-VN')}đ
          </div>
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      sorter: (a: Customer, b: Customer) => {
        const statusOrder = { 'active': 1, 'inactive': 2 };
        return (statusOrder[a.status as keyof typeof statusOrder] || 0) - (statusOrder[b.status as keyof typeof statusOrder] || 0);
      },
      render: (status: string) => {
        const statusConfig = {
          active: { 
            color: '#059669', 
            bgColor: '#d1fae5',
            text: 'Hoạt động',
            icon: 'fas fa-check-circle'
          },
          inactive: { 
            color: '#dc2626', 
            bgColor: '#fee2e2',
            text: 'Không hoạt động',
            icon: 'fas fa-times-circle'
          }
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            padding: '6px 12px',
            borderRadius: '20px',
            backgroundColor: config.bgColor,
            color: config.color,
            fontSize: '12px',
            fontWeight: '500',
            width: 'fit-content'
          }}>
            <i className={config.icon} style={{ fontSize: '10px' }} />
            {config.text}
          </div>
        );
      },
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: Customer) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              icon={<i className="fas fa-eye" />}
              onClick={() => handleView(record)}
              size="small"
              style={{ 
                color: '#6b7280',
                border: 'none',
                boxShadow: 'none'
              }}
            />
          </Tooltip>
          <Tooltip title="Sửa">
            <Button
              type="text"
              icon={<i className="fas fa-edit" />}
              onClick={() => handleEdit(record)}
              size="small"
              style={{ 
                color: '#f59e0b',
                border: 'none',
                boxShadow: 'none'
              }}
            />
          </Tooltip>
          <Tooltip title="Xóa">
            <Button
              type="text"
              icon={<i className="fas fa-trash" />}
              onClick={() => handleDelete(record.id)}
              size="small"
              style={{ 
                color: '#dc2626',
                border: 'none',
                boxShadow: 'none'
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const getSummaryStats = () => {
    const totalCustomers = customersData.pagination?.total || 0;
    const totalValue = allCustomers.reduce((sum: number, c: Customer) => sum + (c.total_amount || 0), 0);

    return (
      <div className={styles.statsContainer}>
        <Row gutter={[24, 16]} className={styles.statsRow}>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#3b82f6' }}>
                  <TeamOutlined />
                </div>
                <div className={styles.statInfo}>
                                     <Statistic
                     title="Tổng khách hàng"
                     value={totalCustomers}
                     valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                   />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#10b981' }}>
                  <CheckCircleOutlined />
                </div>
                <div className={styles.statInfo}>
                                     <Statistic
                     title="Đang hoạt động"
                     value={activeCustomers}
                     valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                   />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#ef4444' }}>
                  <StopOutlined />
                </div>
                <div className={styles.statInfo}>
                                     <Statistic
                     title="Không hoạt động"
                     value={inactiveCustomers}
                     valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                   />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#f59e0b' }}>
                  <DollarOutlined />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Tổng giá trị"
                    value={totalValue}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                    formatter={(value) => value?.toLocaleString('vi-VN')}
                  />
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {getSummaryStats()}

      <Card className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <div className={styles.searchSection}>
            <Search
              placeholder="Tìm kiếm khách hàng..."
              allowClear
              className={styles.searchInput}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
              prefix={<SearchOutlined />}
              size="large"
            />
          </div>
          <div className={styles.actionButtons}>
            <Space size="middle">
              <Tooltip title="Làm mới dữ liệu">
                <Button
                  icon={<i className="fas fa-sync-alt" />}
                  onClick={() => window.location.reload()}
                  className={styles.actionButton}
                >
                  Làm mới
                </Button>
              </Tooltip>
              <Tooltip title="Thêm khách hàng mới">
                <Button
                  type="primary"
                  icon={<i className="fas fa-user-plus" />}
                  onClick={handleAdd}
                  className={styles.primaryButton}
                >
                  Thêm khách hàng
                </Button>
              </Tooltip>
            </Space>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={filteredCustomers}
          rowKey="id"
          loading={isLoading}
          className={styles.customersTable}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: customersData.pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} của ${total} khách hàng`,
            className: styles.tablePagination,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, pageSize) => setPagination({ current: page, pageSize })
          }}
          size="middle"
          scroll={{ x: 800 }}
        />
      </Card>

      {/* Detail Drawer */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileTextOutlined />
            Chi tiết khách hàng
            {isLoadingViewDetail && (
              <Text type="secondary" style={{ fontSize: '14px', marginLeft: '8px' }}>
                (Đang tải...)
              </Text>
            )}
          </div>
        }
        placement="right"
        width={600}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedCustomer(null);
          queryClient.removeQueries(['customer-detail']);
        }}
        open={drawerVisible}
        loading={isLoadingViewDetail}
      >
        {viewDetailData && (
          <div>
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="Mã khách hàng"
                    value={viewDetailData.customer.code}
                    valueStyle={{ fontSize: '16px' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Tổng đơn hàng"
                    value={viewDetailData.customer.total_orders || 0}
                    prefix={<ShoppingOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
              </Row>
            </Card>

            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>Tên khách hàng:</Text>
                  </div>
                  <div>{viewDetailData.customer.name}</div>
                  {viewDetailData.customer.contact_person && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Người liên hệ: {viewDetailData.customer.contact_person}
                    </div>
                  )}
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>Trạng thái:</Text>
                  </div>
                  <Tag color={viewDetailData.customer.status === 'active' ? 'green' : 'red'}>
                    {viewDetailData.customer.status === 'active' ? 'Hoạt động' : 'Không hoạt động'}
                  </Tag>
                </Col>
              </Row>
              {viewDetailData.customer.address && (
                <div style={{ marginTop: 8 }}>
                  <Text strong>Địa chỉ:</Text>
                  <div>{viewDetailData.customer.address}</div>
                </div>
              )}
            </Card>

            <Card size="small" style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>Thông tin liên hệ:</Text>
              </div>
              <List
                dataSource={[
                  { label: 'Số điện thoại', value: viewDetailData.customer.phone, icon: <i className="fas fa-phone" /> },
                  { label: 'Email', value: viewDetailData.customer.email, icon: <MailOutlined /> },
                  { label: 'Mã số thuế', value: viewDetailData.customer.tax_code, icon: <FileTextOutlined /> }
                ].filter(item => item.value)}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={item.icon}
                      title={item.label}
                      description={item.value}
                    />
                  </List.Item>
                )}
              />
            </Card>

            {viewDetailData.orders && viewDetailData.orders.length > 0 && (
              <Card size="small" style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Lịch sử đơn hàng gần đây:</Text>
                </div>
                <List
                  dataSource={viewDetailData.orders}
                  renderItem={(order: any) => (
                    <List.Item>
                      <List.Item.Meta
                        title={`Đơn hàng ${order.voucher_number || order.reference_number}`}
                        description={
                          <div>
                            <div>Ngày: {dayjs(order.created_at).format('DD/MM/YYYY')}</div>
                            <div>Tổng tiền: {(order.total_amount || 0).toLocaleString('vi-VN')}</div>
                            <div>Trạng thái: 
                              <Tag color={order.status === 'completed' ? 'green' : 'orange'} style={{ marginLeft: 4 }}>
                                {order.status === 'completed' ? 'Hoàn thành' : 'Chờ xử lý'}
                              </Tag>
                            </div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            )}

            {viewDetailData.customer.notes && (
              <Card size="small">
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Ghi chú:</Text>
                </div>
                <div>{viewDetailData.customer.notes}</div>
              </Card>
            )}
          </div>
        )}
      </Drawer>

      {/* Customer Modal */}
      <CustomerModal
        visible={modalVisible}
        onCancel={handleModalCancel}
        onSubmit={handleSubmit}
        editingCustomer={editingCustomer}
        loading={createMutation.isLoading || updateMutation.isLoading}
      />
    </div>
  );
};

export default CustomersPage; 